/**
 * Pure helpers behind SymbolSettingsPanel: a settings document section (any
 * strategy's settings object) as a flat list of editable fields, and an
 * immutable field update. Kept generic so a strategy that gains a field is
 * editable without UI changes.
 */

export type FieldValue = boolean | number | string | null | FieldValue[] | { [k: string]: FieldValue };

const TITLES: Record<string, string> = {
  wholeCandle: 'Whole Candle (WCC)',
  pinningVolume: 'Pinning Volume',
  magnetCandle: 'Magnet Candle',
  displacement: 'Displacement',
  diamonds: 'Diamonds',
  ict: 'ICT (FVG)',
  donchian: 'Donchian',
  breakOfStructure: 'Break of Structure',
  fairPrice: 'Fair Price',
  trailingPullback: 'Trailing Pullback',
};

export function describeSection(section: string): { title: string } {
  return { title: TITLES[section] ?? section };
}

/**
 * Top-level fields of a section, `enabled` first, then alphabetical. Nested
 * objects/arrays are one field each (edited as JSON).
 */
export function editableFields(section: Record<string, unknown>): { path: string; value: FieldValue }[] {
  const keys = Object.keys(section).filter((k) => !k.startsWith('_'));
  keys.sort((a, b) => (a === 'enabled' ? -1 : b === 'enabled' ? 1 : a.localeCompare(b)));
  return keys.map((k) => ({ path: k, value: section[k] as FieldValue }));
}

/** A copy of `section` with `path` set (top-level field). */
export function setField(section: Record<string, unknown>, path: string, value: FieldValue): Record<string, unknown> {
  return { ...section, [path]: value };
}
